import { useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Callout,
  DirectionalHint,
  FontIcon,
  Stack,
  Text,
  Link,
  useTheme,
} from '@fluentui/react';
import { notificationsApi, type AppNotificationDto } from '../../api/notificationsApi';
import { useAuth } from '../../auth';

/**
 * Nav bell: shows incomplete (not completed) notifications until source completes them.
 */
export function NotificationBell() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const reactId = useId().replace(/:/g, '');
  const buttonId = `nt-notif-bell-${reactId}`;
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotificationDto[]>([]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setItems([]);
      return;
    }
    try {
      const list = await notificationsApi.getOpen();
      setItems(list);
    } catch (err) {
      console.error('Failed to load open notifications', err);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onChanged = () => { void refresh(); };
    const onFocus = () => { void refresh(); };
    window.addEventListener('ntforms-notifications-changed', onChanged);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('ntforms-notifications-changed', onChanged);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  if (!isAuthenticated) return null;

  const count = items.length;

  return (
    <>
      <button
        id={buttonId}
        type="button"
        title={count > 0 ? `${count} notification(s)` : 'Notifications'}
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'relative',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '8px 12px',
          display: 'inline-flex',
          alignItems: 'center',
          color: theme.palette.neutralPrimary,
        }}
      >
        <FontIcon iconName="Ringer" style={{ fontSize: 18 }} />
        {count > 0 && (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              borderRadius: 8,
              background: theme.palette.themePrimary,
              color: theme.palette.white,
              fontSize: 10,
              lineHeight: '16px',
              textAlign: 'center',
              padding: '0 4px',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>
      {open && (
        <Callout
          target={`#${buttonId}`}
          onDismiss={() => setOpen(false)}
          directionalHint={DirectionalHint.bottomRightEdge}
          setInitialFocus
          styles={{ calloutMain: { width: 340, maxHeight: 400, padding: 12 } }}
        >
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
            Notifications
          </Text>
          {items.length === 0 ? (
            <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>No open notifications</Text>
          ) : (
            <Stack tokens={{ childrenGap: 10 }}>
              {items.map(n => (
                <Stack
                  key={n.id}
                  styles={{
                    root: {
                      borderBottom: `1px solid ${theme.palette.neutralLight}`,
                      paddingBottom: 8,
                    },
                  }}
                >
                  <Text styles={{ root: { fontWeight: 600 } }}>{n.title}</Text>
                  <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                    {n.body}
                  </Text>
                  {n.actionUrl && (
                    <Link
                      onClick={() => {
                        setOpen(false);
                        navigate(n.actionUrl!);
                      }}
                    >
                      {n.actionLabel || 'Open'}
                    </Link>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Callout>
      )}
    </>
  );
}
